import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { quienSoy } from './api';
import { Armazon } from './componentes/Armazon';
import { Cargando } from './componentes/basicos';
import { CambiarClave } from './paginas/CambiarClave';
import { AlistarOrden, Alistamiento } from './paginas/Alistamiento';
import { Camion } from './paginas/Camion';
import { CargarOrdenes } from './paginas/CargarOrdenes';
import { Cau } from './paginas/Cau';
import { Cierre } from './paginas/Cierre';
import { DetalleFactura } from './paginas/DetalleFactura';
import { DetalleOrden } from './paginas/DetalleOrden';
import { Facturas } from './paginas/Facturas';
import { Gastos } from './paginas/Gastos';
import { Ingresar } from './paginas/Ingresar';
import { Inventario } from './paginas/Inventario';
import { Ordenes } from './paginas/Ordenes';
import { Patroclo } from './paginas/Patroclo';
import { PatrocloDocumento, PatrocloDocumentos } from './paginas/PatrocloDocumentos';
import { PatrocloTraza, PatrocloTrazas } from './paginas/PatrocloTrazas';
import { Usuarios } from './paginas/Usuarios';

export function App(): ReactNode {
  const cliente = useQueryClient();

  /*
   * `GET /auth/yo` es la fuente de verdad de la sesión, no una bandera
   * guardada en el navegador: la sesión vive en el servidor y puede haberse
   * revocado sin que esta pestaña se entere.
   */
  const sesion = useQuery({
    queryKey: ['yo'],
    queryFn: quienSoy,
    retry: false,
  });

  if (sesion.isLoading) return <Cargando que="la sesión" />;

  if (sesion.isError || !sesion.data) {
    return <Ingresar alEntrar={() => void cliente.invalidateQueries({ queryKey: ['yo'] })} />;
  }

  /*
   * Con la contraseña inicial sin cambiar no se llega a ninguna pantalla. No
   * es cortesía: el backend rechaza todo lo demás (S-18), así que mostrar el
   * resto solo produciría una aplicación llena de errores 403.
   */
  if (sesion.data.debeCambiarClave) {
    return (
      <CambiarClave
        obligado
        alCambiar={() => void cliente.invalidateQueries({ queryKey: ['yo'] })}
      />
    );
  }

  return (
    <Armazon
      usuario={sesion.data}
      alSalir={() => void cliente.invalidateQueries({ queryKey: ['yo'] })}
    >
      <Routes>
        <Route path="/" element={<Navigate to="/ordenes" replace />} />
        <Route path="/ordenes" element={<Ordenes />} />
        <Route path="/ordenes/:id" element={<DetalleOrden />} />
        <Route path="/cargar" element={<CargarOrdenes />} />
        {/*
          La cartera no es de los operativos. El repartidor ve los precios de lo
          que entrega —eso es contexto— pero la plata del negocio no es suya, y
          el backend responde 403 igual (docs/SEGURIDAD.md, S-08).
        */}
        {sesion.data.rol !== 'REPARTIDOR' && sesion.data.rol !== 'PRODUCTOR' && (
          <>
            <Route path="/plata" element={<Facturas />} />
            <Route path="/plata/:id" element={<DetalleFactura />} />
            {/* F8: el cierre de mes. Mismos roles que la cartera — es lectura
                financiera, y el contador es quien cierra el mes. */}
            <Route path="/cierre" element={<Cierre />} />
          </>
        )}
        {/* F4: del repartidor. El contador no toca inventario. */}
        {sesion.data.rol !== 'PRODUCTOR' && sesion.data.rol !== 'CONTADOR' && (
          <>
            <Route path="/alistar" element={<Alistamiento />} />
            <Route path="/alistar/:id" element={<AlistarOrden />} />
          </>
        )}
        {/*
          F9: el inventario es de quien lo mueve. El comercial no lo toca y el
          contador tampoco — mismos roles que `InventarioController`.
        */}
        {(sesion.data.rol === 'ADMINISTRADOR' ||
          sesion.data.rol === 'PRODUCTOR' ||
          sesion.data.rol === 'REPARTIDOR' ||
          sesion.data.rol === 'DESARROLLADOR') && (
          <Route path="/inventario" element={<Inventario />} />
        )}
        {/*
          El camión: lo digita el administrador, pero lo lee todo el que reparte.
          Quien ve que un día está bloqueado tiene que poder ver por qué.
        */}
        {sesion.data.rol !== 'PRODUCTOR' && sesion.data.rol !== 'CONTADOR' && (
          <Route path="/camion" element={<Camion />} />
        )}
        {/* El CAU es de quien responde por la entrega, no del contador. */}
        {(sesion.data.rol === 'ADMINISTRADOR' ||
          sesion.data.rol === 'COMERCIAL_FINANCIERO' ||
          sesion.data.rol === 'DESARROLLADOR') && (
          <Route path="/cau" element={<Cau />} />
        )}
        {/*
          Patroclo, el asistente (E8-01). Los mismos roles que el proxy
          (`PatrocloController`) y que `ROLES_HABILITADOS` en Patroclo: abrirlo a
          otro rol es cambiar las tres listas a la vez.
        */}
        {(sesion.data.rol === 'ADMINISTRADOR' || sesion.data.rol === 'DESARROLLADOR') && (
          <>
            <Route path="/patroclo" element={<Patroclo />} />
            {/* `documentos` es un segmento fijo: React Router lo prefiere a `:id`. */}
            <Route path="/patroclo/documentos" element={<PatrocloDocumentos />} />
            <Route path="/patroclo/documentos/:id" element={<PatrocloDocumento />} />
            {/*
              Las trazas, igual: antes que `:id`. Sin estas rutas, `/patroclo/trazas`
              caía en el chat como si «trazas» fuera una conversación.
            */}
            <Route path="/patroclo/trazas" element={<PatrocloTrazas />} />
            <Route path="/patroclo/trazas/:id" element={<PatrocloTraza />} />
            <Route path="/patroclo/:id" element={<Patroclo />} />
          </>
        )}
        {/* Lo que cuesta operar el sistema: de quien lo opera, no del negocio
            (E8-02). Mismos roles que `GastosController`. */}
        {(sesion.data.rol === 'ADMINISTRADOR' || sesion.data.rol === 'DESARROLLADOR') && (
          <Route path="/gastos" element={<Gastos />} />
        )}
        <Route
          path="/clave"
          element={
            <CambiarClave
              obligado={false}
              alCambiar={() => void cliente.invalidateQueries({ queryKey: ['yo'] })}
            />
          }
        />
        {/*
          La ruta solo existe para el administrador. Esconderla no es el
          control —el backend exige el rol igual—, pero enseñar una pantalla
          que va a responder 403 tampoco ayuda a nadie.
        */}
        {sesion.data.rol === 'ADMINISTRADOR' && (
          <Route path="/usuarios" element={<Usuarios />} />
        )}
        <Route path="*" element={<Navigate to="/ordenes" replace />} />
      </Routes>
    </Armazon>
  );
}
